import { usePendingValuationRefresh } from '../hooks/usePendingValuationRefresh';
import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import { supabase } from '../lib/supabase';
import { type UserProductWithDetails, parseImageAnalysis } from '../types/newDatabaseTypes';
import type { CacheStackParamList, RootStackParamList } from '../types/navigation';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { ItemValuation } from '../components/common/ItemValuation';
import { ItemSpecificValuation } from '../components/common/ItemSpecificValuation';
import { IdentityQuestionCard } from '../components/common/IdentityQuestion';
import { hasOpenIdentityQuestion } from '../lib/itemIdentity';
import { ConditionBadge } from '../components/common/ConditionBadge';
import { VariantPicker } from '../components/common/VariantPicker';
import { getUserProductById, updateUserProduct } from '../utils/userProductStorage';
import { listSiblingVariants, type SiblingVariant } from '../lib/variantOptions';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../context/AuthContext';
import { isUserAdmin } from '../utils/userPermissions';
import { AdminCompsSection } from '../components/admin/AdminCompsSection';
import OwnedDeviceBuyLink from '../components/buy/OwnedDeviceBuyLink';

type Props = {
  navigation: NativeStackNavigationProp<CacheStackParamList, 'ItemDetail'>;
  route: RouteProp<CacheStackParamList, 'ItemDetail'>;
};

export default function ItemDetailScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        scrollView: {
          flex: 1,
        },
        centered: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        header: {
          paddingVertical: theme.spacing.lg,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        headerTitle: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        statusText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.md,
        },
        errorText: {
          ...theme.typography.md,
          color: theme.colors.destructive,
          textAlign: 'center',
        },
        nameHeader: {
          paddingVertical: theme.spacing.lg,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        itemName: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        imageCarouselContainer: {
          height: 300,
          backgroundColor: theme.colors.backgroundSurface,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          position: 'relative',
        },
        imageCarousel: {
          flex: 1,
        },
        imageSlide: {
          width: SCREEN_WIDTH,
          height: 300,
          justifyContent: 'center',
          alignItems: 'center',
        },
        image: {
          width: '100%',
          height: '100%',
          resizeMode: 'cover',
        },
        imageControls: {
          position: 'absolute',
          bottom: theme.spacing.md,
          left: 0,
          right: 0,
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: theme.spacing.lg,
        },
        paginationDots: {
          flexDirection: 'row',
          gap: theme.spacing.xs,
        },
        paginationDot: {
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: 'rgba(242, 237, 228, 0.4)',
        },
        paginationDotActive: {
          backgroundColor: theme.colors.accent,
        },
        imageCounter: {
          backgroundColor: 'rgba(13, 13, 13, 0.7)',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          paddingHorizontal: theme.spacing.md,
          paddingVertical: theme.spacing.xs,
          borderRadius: theme.radius.radiusLg,
        },
        imageCounterText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          letterSpacing: 0,
        },
        formContainer: {
          padding: theme.spacing.lg,
        },
        fieldGroup: {
          marginBottom: theme.spacing.lg,
        },
        variantPickerSection: {
          marginBottom: theme.spacing.lg,
          gap: theme.spacing.sm,
        },
        variantPickerButton: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          paddingVertical: theme.spacing.sm,
        },
        variantPickerButtonText: {
          ...theme.typography.sm,
          color: theme.colors.accent,
        },
        variantPickerHeading: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        variantPickerCancel: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          paddingVertical: theme.spacing.sm,
        },
        readOnlyFieldMultiline: {
          minHeight: 80,
        },
        valuationField: {
          paddingVertical: theme.spacing.lg,
        },
        valuationRow: {
          flexDirection: 'row',
          alignItems: 'baseline',
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.xs,
        },
        valuationAmount: {
          ...theme.typography.valueSm,
          color: theme.colors.accent,
        },
        valuationCaption: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          fontStyle: 'italic',
          letterSpacing: 0,
        },
        valuationRange: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        valuationWarning: {
          ...theme.typography.xs,
          color: theme.colors.destructive,
          marginTop: theme.spacing.xs,
          letterSpacing: 0,
        },
        valuationLoadingRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginTop: theme.spacing.xs,
        },
        valuationLoadingText: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          letterSpacing: 0,
        },
        conditionWrapper: {
          marginTop: theme.spacing.sm,
        },
        actionChipsInline: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: theme.spacing.sm,
          marginTop: theme.spacing.xl,
          marginBottom: theme.spacing.lg,
        },
        actionChip: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: 'transparent',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        actionChipDanger: {
          borderColor: theme.colors.destructive,
        },
        actionChipText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        actionChipTextDanger: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.destructive,
        },
      }),
    [theme],
  );
  const { itemId } = route.params;
  const rootNavigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { session } = useAuth();
  const [item, setItem] = useState<UserProductWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [valuationRefreshError, setValuationRefreshError] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [siblingVariants, setSiblingVariants] = useState<SiblingVariant[]>([]);
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const [savingVariant, setSavingVariant] = useState(false);
  const isFocused = useIsFocused();
  const MAX_POLL_ATTEMPTS = 24; // 24 × 5s = 2 minutes

  useEffect(() => {
    if (!isFocused) return;
    let active = true;
    void fetchItem(() => active);
    return () => { active = false; };
  }, [itemId, isFocused, session?.user?.id]);

  // Load the catalogued sibling variants of this item's model so the user can
  // correct the variant (T4a). Hidden unless ≥2 exist (Q2). modelId comes from
  // the joined variant; the current variant_id marks `isCurrent`.
  const modelId = item?.variant?.model_id ?? item?.variant?.model?.id ?? null;
  useEffect(() => {
    if (!modelId) {
      setSiblingVariants([]);
      return;
    }
    let cancelled = false;
    listSiblingVariants(modelId, item?.variant_id ?? null).then((variants) => {
      if (!cancelled) setSiblingVariants(variants);
    });
    return () => {
      cancelled = true;
    };
  }, [modelId, item?.variant_id]);

  const handleVariantSelect = async (variant: SiblingVariant) => {
    if (!item || variant.variantId === item.variant_id) {
      setShowVariantPicker(false);
      return;
    }
    setSavingVariant(true);
    // variant_source='user' marks the pick as a user assertion (C5). V3 P7: no
    // valuation/currency co-write — the sibling's catalogue price is not this item's value.
    const updated = await updateUserProduct(item.id, {
      variant_id: variant.variantId,
      variant_source: 'user',
    });
    if (updated) setItem(updated);
    setSavingVariant(false);
    setShowVariantPicker(false);
  };

  // Poll for variant_id when it's still null (backend hasn't finished catalog matching).
  // - Bounded: stops after MAX_POLL_ATTEMPTS so a stuck instrument doesn't poll forever.
  // - Focus-gated: pauses when the screen is blurred (React Navigation keeps the
  //   screen mounted in the stack, so without this we'd burn RPCs in the background).
  //   When focus returns, the effect re-runs and the interval resumes.
  useEffect(() => {
    if (loading || !item || item.restoration_valuation || item.variant_id || !isFocused) return;
    if (pollAttempts >= MAX_POLL_ATTEMPTS) return;
    const interval = setInterval(() => {
      setPollAttempts(n => n + 1);
      fetchItem();
    }, 5000);
    return () => clearInterval(interval);
  }, [loading, item?.variant_id, item?.restoration_valuation, pollAttempts, isFocused]);

  const workStatus = usePendingValuationRefresh(Boolean(item?.restoration_pending), isFocused, async active => {
    const product = await getUserProductById(itemId);
    if (!active()) return;
    setValuationRefreshError(!product);
    if (product) setItem(product);
    return Boolean(product);
  }, `${session?.user?.id}:${itemId}`, item?.restoration_valuation ? [itemId] : []);

  const imageAnalysis = parseImageAnalysis(item?.image_analysis);

  useEffect(() => {
    if (session) {
      isUserAdmin().then(setIsAdmin);
    }
  }, [session]);

  useEffect(() => {
    if (item?.images) {
      getImageUrl();
    }
  }, [item?.images]);

  const getImageUrl = async () => {
    if (!item?.images) return;

    try {
      // Check if images is an array and has at least one item
      const images = item.images as any;
      if (images && Array.isArray(images) && images.length > 0) {
        console.log(`Loading ${images.length} image(s)`);

        // Get public URLs for all images
        const urls: string[] = [];
        for (const img of images) {
          const imagePath = img.storage_path || img;
          console.log('Image path:', imagePath);

          const { data } = supabase
            .storage
            .from('item-images')
            .getPublicUrl(imagePath);

          if (data?.publicUrl) {
            console.log('Image URL:', data.publicUrl);
            urls.push(data.publicUrl);
          } else {
            console.warn('No public URL returned for image');
          }
        }

        setImageUrls(urls);
        console.log(`Loaded ${urls.length} image URL(s)`);
      } else {
        console.warn('No images found or images is not an array');
      }
    } catch (error) {
      console.error('Error getting image URLs:', error);
    }
  };

  const fetchItem = async (active: () => boolean = () => true) => {
    if (!itemId) {
      setError('No item ID provided.');
      return;
    }
    const background = item?.id === itemId && (!session || item.user_id === session.user.id);
    if (!background) setLoading(true);
    setError(null);
    console.log(`Fetching user product with ID: ${itemId}`);
    try {
      // Use the utility function to get the user product with all related details
      const product = await getUserProductById(itemId);

      if (!active()) return;
      if (!product) {
        throw new Error('Product not found');
      }

      console.log('User product data:', product);
      setItem(product);
      setValuationRefreshError(false);
    } catch (err) {
      console.error('Error fetching user product:', err);
      if (!active()) return;
      if (background) setValuationRefreshError(true);
      else setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      if (active()) setLoading(false);
    }
  };

  const handleDelete = async () => {
    console.log('Delete button pressed');
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      console.log('Delete confirmed, starting delete...');
      setDeleting(true);

      // Use the deleteUserProduct function from userProductStorage.ts
      // But for now, we'll use direct Supabase call
      const { error } = await supabase
        .from('user_products')
        .delete()
        .match({ id: itemId });

      if (error) {
        throw error;
      }

      console.log('Product deleted successfully');
      navigation.goBack();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete product');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleEditPress = () => {
    if (item) {
      navigation.navigate('EditExistingItem', { itemId: item.id });
    }
  };

  const handleRemovePress = () => {
    if (item) {
      rootNavigation.navigate('RemoveItem', { itemId: item.id });
    }
  };

  const renderField = (label: string, value: React.ReactNode, multiline = false) => (
    <View style={styles.fieldGroup}>
      <Text style={commonStyles.inputLabel}>{label}</Text>
      <View style={[commonStyles.readOnlyField, multiline && styles.readOnlyFieldMultiline]}>
        {typeof value === 'string' || typeof value === 'number'
          ? <Text style={commonStyles.readOnlyText}>{value}</Text>
          : value}
      </View>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={commonStyles.safeArea}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Item Details</Text>
        </View>
        <View style={styles.centered}>
          <ActivityIndicator size={36} color={theme.colors.accent} />
          <Text style={styles.statusText}>Loading item details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={commonStyles.safeArea}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Item Details</Text>
        </View>
        <View style={styles.centered}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!item) {
    return (
      <SafeAreaView style={commonStyles.safeArea}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Item Details</Text>
        </View>
        <View style={styles.centered}>
          <Text style={styles.errorText}>Item not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={commonStyles.safeArea} edges={['top']}>
      <ScrollView style={styles.scrollView}>
        {/* Item Name Header */}
        <View style={styles.nameHeader}>
          <Text style={styles.itemName}>
            {(item?.variant?.model?.family?.brand?.name || imageAnalysis.brand) &&
             (item?.variant?.model?.name || imageAnalysis.model)
              ? `${item?.variant?.model?.family?.brand?.name || imageAnalysis.brand} ${item?.variant?.model?.name || imageAnalysis.model}`
              : 'Product Details'}
          </Text>
        </View>

        {imageUrls.length > 0 && (
          <View style={styles.imageCarouselContainer}>
            <ScrollView
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onMomentumScrollEnd={(event) => {
                const index = Math.round(event.nativeEvent.contentOffset.x / event.nativeEvent.layoutMeasurement.width);
                setCurrentImageIndex(index);
              }}
              style={styles.imageCarousel}
            >
              {imageUrls.map((url, index) => (
                <View key={index} style={styles.imageSlide}>
                  <Image source={{ uri: url }} style={styles.image} />
                </View>
              ))}
            </ScrollView>

            {/* Image counter and pagination dots */}
            {imageUrls.length > 1 && (
              <View style={styles.imageControls}>
                <View style={styles.paginationDots}>
                  {imageUrls.map((_, index) => (
                    <View
                      key={index}
                      style={[
                        styles.paginationDot,
                        index === currentImageIndex && styles.paginationDotActive,
                      ]}
                    />
                  ))}
                </View>
                <View style={styles.imageCounter}>
                  <Text style={styles.imageCounterText}>
                    {currentImageIndex + 1} / {imageUrls.length}
                  </Text>
                </View>
              </View>
            )}
          </View>
        )}

        <View style={styles.formContainer}>
          <View style={styles.fieldGroup}>
            <Text style={commonStyles.inputLabel}>Valuation</Text>
            {valuationRefreshError && <View>
              <Text accessibilityRole="alert">{item?.restoration_pending ? 'Unable to refresh valuation. Retrying…' : 'Unable to refresh valuation. Please try again.'}</Text>
              <TouchableOpacity onPress={() => { void fetchItem(); }}><Text>Retry</Text></TouchableOpacity>
            </View>}
            <View style={[commonStyles.readOnlyField, styles.valuationField]}>
              {/* V3 P6: the item's own valuation state only. The catalogue headline and its
                  price history are Buy / catalogue surfaces (DESIGN_V3 §5.5). */}
              {item &&
                (!item.restoration_valuation && item.identity_reason?.startsWith('scan-v1:') ? (
                  <ItemSpecificValuation
                    item={item}
                    token={session?.access_token ?? null}
                    focused={isFocused}
                    onConfirmed={fetchItem}
                  />
                ) : (
                  <ItemValuation item={item} workPending={workStatus[item.id]} />
                ))}
            </View>
            {/* T1: a saved item with an open identity question can be resolved here. */}
            {item && hasOpenIdentityQuestion(item.identity_reason) && (
              <IdentityQuestionCard productId={item.id} onResolved={fetchItem} />
            )}
          </View>

          {renderField('Brand', item?.variant?.model?.family?.brand?.name || imageAnalysis.brand || 'N/A')}
          {renderField('Model', item?.variant?.model?.name || imageAnalysis.model || 'N/A')}
          {renderField('Family', item?.variant?.model?.family?.name || imageAnalysis.family || 'N/A')}

          {renderField(
            'Variant Attributes',
            (() => {
              // Debug: Log the variant object
              console.log('[ItemDetail] Variant data:', JSON.stringify(item?.variant, null, 2));

              // Try database variant attributes first (stored as JSON object)
              const attrs = item?.variant?.attributes as any;
              if (attrs && typeof attrs === 'object' && Object.keys(attrs).length > 0) {
                console.log('[ItemDetail] Found attributes:', attrs);
                return Object.entries(attrs)
                  .map(([key, value]) => {
                    // Capitalize first letter and replace underscores with spaces
                    const displayKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
                    return `${displayKey}: ${value}`;
                  })
                  .join('\n');
              }
              console.log('[ItemDetail] No variant attributes, checking image_analysis');
              // Fallback to Gemini-detected variant fields from image_analysis
              const variantFields = (item?.image_analysis as any)?.variant_fields;
              if (variantFields && Array.isArray(variantFields) && variantFields.length > 0) {
                console.log('[ItemDetail] Using image_analysis variant_fields:', variantFields);
                return variantFields.map((v: any) => `${v.name}: ${v.value}`).join('\n');
              }
              console.log('[ItemDetail] No variant data found');
              return 'N/A';
            })(),
            true,
          )}

          {/* Choose the right variant (T4a). Shown only when the catalog has
              ≥2 sibling variants for this model (Q2). Lets the user correct a
              mis-identified variant by picking an existing, already-priced one
              — no free-text, no re-identification. */}
          {siblingVariants.length >= 2 && (
            <View style={styles.variantPickerSection}>
              {!showVariantPicker ? (
                <TouchableOpacity
                  testID="open-variant-picker"
                  accessibilityRole="button"
                  style={styles.variantPickerButton}
                  onPress={() => setShowVariantPicker(true)}
                  disabled={savingVariant}
                >
                  <Ionicons
                    name="swap-horizontal-outline"
                    size={18}
                    color={theme.colors.accent}
                  />
                  <Text style={styles.variantPickerButtonText}>
                    Choose the right variant
                  </Text>
                </TouchableOpacity>
              ) : (
                <>
                  <Text style={styles.variantPickerHeading}>Choose the right variant</Text>
                  <VariantPicker showPrices={!item?.restoration_valuation} variants={siblingVariants} onSelect={handleVariantSelect} />
                  <TouchableOpacity
                    testID="cancel-variant-picker"
                    accessibilityRole="button"
                    onPress={() => setShowVariantPicker(false)}
                    disabled={savingVariant}
                  >
                    <Text style={styles.variantPickerCancel}>Cancel</Text>
                  </TouchableOpacity>
                </>
              )}
              {savingVariant && (
                <ActivityIndicator size="small" color={theme.colors.accent} />
              )}
            </View>
          )}

          {renderField('Description', (item?.image_analysis as any)?.description || 'N/A', true)}
          {renderField('Category', (item?.image_analysis as any)?.category || 'N/A')}

          {renderField(
            'AI Analysis Scores',
            `${(item?.image_analysis as any)?.confidence_score !== undefined
              ? `Confidence: ${((item.image_analysis as any).confidence_score * 100).toFixed(0)}%`
              : 'Confidence: N/A'}\n${(item?.image_analysis as any)?.condition_score !== undefined
                ? `Condition: ${((item.image_analysis as any).condition_score * 100).toFixed(0)}%`
                : 'Condition: N/A'}\n${(item?.image_analysis as any)?.demand_score !== undefined
                  ? `Demand: ${((item.image_analysis as any).demand_score * 100).toFixed(0)}%`
                  : 'Demand: N/A'}`,
          )}

          {renderField('Status', item?.status || 'N/A')}

          <View style={styles.fieldGroup}>
            <Text style={commonStyles.inputLabel}>Condition</Text>
            <View style={styles.conditionWrapper}>
              <ConditionBadge condition={item?.condition || 'N/A'} />
            </View>
          </View>

          {renderField('Purchase Price', `$${item?.purchase_price?.toFixed(2) || 'N/A'}`)}
          {renderField('Purchase Period', item?.purchase_period || 'N/A')}
          {renderField('Currency', item?.currency || 'USD')}

          {renderField(
            'Upload Date',
            item?.created_at
              ? new Date(item.created_at).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'N/A',
          )}

          {renderField(
            'Last Updated',
            item?.updated_at
              ? new Date(item.updated_at).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'N/A',
          )}

          {/* Action buttons - low priority, at bottom of scroll */}
          <View style={styles.actionChipsInline}>
            <TouchableOpacity
              style={styles.actionChip}
              onPress={handleEditPress}
            >
              <Ionicons name="create-outline" size={16} color={theme.colors.textPrimary} />
              <Text style={styles.actionChipText}>Edit</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionChip}
              onPress={() => rootNavigation.navigate('ReportIssue', { productId: itemId })}
            >
              <Ionicons name="flag-outline" size={16} color={theme.colors.textPrimary} />
              <Text style={styles.actionChipText}>Report</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionChip, styles.actionChipDanger]}
              onPress={handleRemovePress}
            >
              <Ionicons name="trash-outline" size={16} color={theme.colors.destructive} />
              <Text style={styles.actionChipTextDanger}>Remove</Text>
            </TouchableOpacity>
          </View>

          {/* Admin: deep-link to the imported catalog listing for this device */}
          {isAdmin && item?.variant_id && (
            <OwnedDeviceBuyLink variantId={item.variant_id} />
          )}

          {/* Admin: Comparable Listings */}
          {isAdmin && item?.variant_id && (
            <AdminCompsSection variantId={item.variant_id} />
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

